import { useAtomValue, useSetAtom } from "jotai";
import { Timer } from "lucide-react";
import { useCallback, useId, useState } from "react";
import Modal from "@/components/Modal";
import { createDeviceMessageRequestAtom } from "@/store/crud";
import { deviceToLicMapAtom } from "@/store/data";
import { useToast } from "./Toast";
import Button from "./ui/Button";

type DeviceActivationPanelProps = {
  elementType: "pump" | "valve";
  elementId: string;
  // Need to find the LIC
  elementControllerId: string;
  elementVariation?: "backwash" | undefined;
  turnOnDurationMinutes: number;
  title?: string;

  status?: "on" | "off"; 
  remainingPumpTimeMinutes?: number | null;
  onTurnOffRequest?: (confirmTurnOff: () => void) => void;
};

export function DeviceActivationPanel({
  elementType,
  elementId,
  elementControllerId,
  elementVariation,
  turnOnDurationMinutes,
  title,
  status,
  remainingPumpTimeMinutes,
  onTurnOffRequest,
}: DeviceActivationPanelProps) {
  const createDeviceMessageRequest = useSetAtom(createDeviceMessageRequestAtom);
  const deviceToLicMap = useAtomValue(deviceToLicMapAtom);
  const { showInfo, showWarning } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [duration, setDuration] = useState<string>(String(turnOnDurationMinutes));
  const durationId = useId();

  const handleTestActivation = useCallback(
    async (turnOn: boolean, durationOverride?: number) => {
      const lic = deviceToLicMap.get(elementControllerId);
      if (!lic) {
        showWarning({
          message: `Controlador de Irrigação não encontrado para o ${
            elementType === "pump" ? "controlador de bomba" : "controlador de válvula"
          }.`,
        });
        return;
      }

      const finalDuration = durationOverride ?? turnOnDurationMinutes;

      await createDeviceMessageRequest({
        device: lic,
        payload_type: "control",
        payload_data: {
          elementType,
          elementId,
          elementVariation,
          turnOn,
          durationMinutes: turnOn ? finalDuration : undefined,
        },
      });
      showInfo({
        message: `Ativação de ${elementType === "pump" ? "bomba" : "válvula"} enviada com sucesso.`,
      });
    },
    [
      elementType,
      elementId,
      elementControllerId,
      elementVariation,
      turnOnDurationMinutes,
      createDeviceMessageRequest,
      deviceToLicMap,
    ]
  );

  const handleTestBackwash = useCallback(
    async (turnOn: boolean, durationOverride?: number) => {
      const lic = deviceToLicMap.get(elementControllerId);
      if (!lic) {
        showWarning({
          message: `Controlador de Irrigação não encontrado para o controlador de válvula.`,
        });
        return;
      }

      const finalDuration = durationOverride ?? turnOnDurationMinutes;

      await createDeviceMessageRequest({
        device: lic,
        payload_type: "control",
        payload_data: {
          elementType: "valve",
          elementId,
          elementVariation: "backwash",
          turnOn,
          durationMinutes: turnOn ? finalDuration : undefined,
        },
      });
      showInfo({
        message: `Ativação de retrolavagem enviada com sucesso.`,
      });
    },
    [elementId, elementControllerId, turnOnDurationMinutes, createDeviceMessageRequest, deviceToLicMap]
  );

  const handleTurnOnClick = () => {
    setDuration(String(turnOnDurationMinutes));
    setIsModalOpen(true);
  };

  const handleConfirmTurnOn = () => {
    const durationNum = parseInt(duration);
    if (isNaN(durationNum) || durationNum <= 0) {
      return;
    }

    if (elementVariation === "backwash") {
      handleTestBackwash(true, durationNum);
    } else {
      handleTestActivation(true, durationNum);
    }
    setIsModalOpen(false);
  };

  const handleTurnOffClick = () => {
    const executeTurnOff = () => {
      elementVariation === "backwash" ? handleTestBackwash(false) : handleTestActivation(false);
    };

    if(onTurnOffRequest){
      onTurnOffRequest(executeTurnOff);
      return;
    }
    executeTurnOff();
  }

  return (
    <>
      <div className={`flex flex-col border rounded-lg p-2 ${status? "border-gray-400" : "border-gray-200 "}`}>
        <div className="flex flex-col items-start justify-between gap-2">
          {status && (
            <div className="flex items-center justify-start gap-1">
              <div
                className={`h-4 w-4 rounded-full border-1 ${
                  status === "on" ? "border-green-800 bg-green-500" : "border-red-800 bg-red-500"
                }`}
              />
              <div
                className={`px-1 py-0.5 rounded text-xs font-bold ${
                  status === "on"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {status === "on" ? "LIGADO" : "DESLIGADO"}
              </div>
            </div>
          )}
          <div className="w-full flex justify-center">
            <div className={`text-sm text-gray-700 text-center ${status ? "font-bold text-[16px]" : "font-medium"}`}>{title ?? "Testar Acionamento"}</div>
          </div>
        </div>

        <div className={`flex gap-2 justify-center mt-2 ${status? "flex-col mt-4":"flex-row"}`}>
          <Button variant={status? "primary" : "info"} outline type="button" className="flex-1 h-5" size={status? "sm" : "md"} onClick={handleTurnOnClick}>
            Ligar
          </Button>
          <Button
            variant="destructive"
            outline
            type="button"
            className="flex-1"
            size={status? "sm" : "md"}
            onClick={handleTurnOffClick}
          >
            Desligar
          </Button>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Acionamento Manual" size="sm">
        <div className="space-y-4">
          {remainingPumpTimeMinutes && status && <h3>
            Faltam <strong>{remainingPumpTimeMinutes + " min"}</strong> para a bomba desligar. Não ter válvula acionada nesse período pode acarretar problemas.
          </h3>}
          <div>
            <label htmlFor={durationId} className="block text-sm font-medium text-gray-700 mb-1">
              Duração (minutos)
            </label>
            <div className="relative">
              <Timer className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                id={durationId}
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="30"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirmTurnOn} disabled={!duration || parseInt(duration) <= 0}>
              Ligar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
